/* eslint-disable camelcase */
import React, { useMemo, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { Grid, makeStyles } from '@rbx/ui';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import AppLayout, { TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import useHeadingGroupStyles from '@modules/ui/shared/styles/HeadingGroup.styles';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import AppRightNavContent from '@modules/ui/shared/components/AppRightNavContent';
import Heading from '@modules/utilities/interfaces/Heading';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import ReleaseNoteComponent from '@modules/article/components/ReleaseNoteComponent';
import { ReleaseNoteContentType } from '@modules/utilities/pageBuild/content';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import styles from '@modules/ui/shared/utils/styles';
import { TestId } from '@modules/utilities/constants/testConstants';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import ArticleNavigationSection, {
  ArticleNavigationSectionProps,
} from './ArticleNavigationSection';
import ReleaseNoteType from '../enum/ReleaseNoteType';

const useReleaseNoteContainerStyles = makeStyles()(() => ({
  container: styles.wordBreakWrap,
}));

export interface ReleaseNoteContainerProps extends ArticleNavigationSectionProps, Heading {
  title: string;
  content: ReleaseNoteContentType[];
  openGraphMeta: Partial<TOpenGraphMeta>;
  slug?: string;
  releaseNoteTypeList: ReleaseNoteType[];
}

const ReleaseNoteContainer: FunctionComponent<ReleaseNoteContainerProps> = ({
  slug,
  title,
  content,
  next,
  prev,
  openGraphMeta,
  releaseNoteTypeList,
}) => {
  const {
    classes: { articleHeader },

    cx,
  } = useHeadingGroupStyles();
  const {
    classes: { container },
  } = useReleaseNoteContainerStyles();
  const { getBreadcrumbsItems } = useSideBarContent();
  const { translate } = useTranslation();
  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);

  const headings = useMemo(() => {
    const headingItems: HeadingItem[] = [];
    releaseNoteTypeList.forEach((type) => {
      headingItems.push({
        title: translate(`Heading.${type}`),
        slug: type,
        level: 2,
      });
    });
    return headingItems;
  }, [releaseNoteTypeList, translate]);

  const getVersionNumber = useMemo(() => {
    const match = title.match(/\d+$/);
    const versionNumber = match ? parseInt(match[0], 10).toString() : '';
    return versionNumber;
  }, [title]);

  const pageMetaDescription = useMemo(() => {
    return translate('Message.ReleaseNoteDescription', { versionNumber: getVersionNumber });
  }, [getVersionNumber, translate]);

  return (
    <AppLayout
      testid={TestId.ReleaseNoteContainer}
      pageTitle={title}
      pageMetaDescription={pageMetaDescription}
      headings={headings}
      openGraphMeta={openGraphMeta}
      slug={slug}>
      <AppMainNavbar pageTitle={title} breadcrumbsItems={breadcrumbsItems} />
      <AppHeading title={title} />
      <AppIAHeading breadcrumbsItems={breadcrumbsItems} />
      <AppRightNav content={<AppRightNavContent allowExpand={false} />} />
      <AppContent>
        <Grid
          component='article'
          container
          direction='column'
          wrap='nowrap'
          className={cx(container, articleHeader)}>
          <ReleaseNoteComponent
            releaseNoteContents={content}
            releaseNoteTypeList={releaseNoteTypeList}
            versionNumber={getVersionNumber}
          />
        </Grid>
        <ArticleNavigationSection next={next} prev={prev} />
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default ReleaseNoteContainer;
